/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useState } from "react";
import {
	Progress,
  Segmented,
  Tooltip
} from "antd";
import { AGGREGATE_OPTIONS } from "../Comparison1V1";
import { getPercentageValues } from "../../../Utils/chart_utils"
import { getTextColorBasedOnBg } from '../../../Utils/color_utils'

export const AggregateRow = ({ data, handleMetricAnnotationChange, dateRangeIds, showHeader = false, dateRangeIdToLabelMap, annotation = 'avg' })  => {

  const [selectedAggregateOption, setSelectedAggregateOption] = useState(AGGREGATE_OPTIONS.AVG);
  const [metricIdentifier, setMetricIdentifier] = useState(null);
  const [dataToDisplay, setDataToDisplay] = useState(null);

  useEffect(() => {

    const metric = dateRangeIds?.length ? dateRangeIds[0] : null;
    const decimals = metric?.num_decimals || metric?.decimal_places || 2;
    const low_good = data?.range1?.metric?.low_good || false;
    const rawValues = [];

    let aggregateDataToDisplay = {
      colors: data?.colors,
      name: data.name,
      identifier: data.identifier,
      decimal_places: decimals,
      low_good
    }

    dateRangeIds.forEach( (rangeId) => {
      const rawValue = data?.[rangeId]?.values?.[selectedAggregateOption] || 0;
      rawValues.push(rawValue)
    })

    dateRangeIds.forEach( (rangeId, index) => {
      const rawValue = rawValues[index]
      const percent = getPercentageValues(rawValue, decimals, rawValues, low_good);
      aggregateDataToDisplay = {
        ...aggregateDataToDisplay,
        [rangeId]: {
          rawValue,
          percent
        }
      }
    })

    setDataToDisplay(aggregateDataToDisplay);
    setMetricIdentifier(data.identifier);
  }, [data, selectedAggregateOption])

  const handleAggregateOptionChange = (event) => {
    handleMetricAnnotationChange(metricIdentifier, event);
    setSelectedAggregateOption(event);
  }

  return (
    <>
      <div className="aggregate-row-container-selections">
        <div className={showHeader ? "aggregate-selection-container-with-header" : ""}>
          { showHeader && (
            <div className="aggregate-row-header"></div>
          )}

          <div className="aggregate-selection-row">
            <Tooltip title={dataToDisplay?.name || ''}>
            <span className="segmented-label">{dataToDisplay?.name || ''}</span>
            </Tooltip>
            <Segmented options={Object.values(AGGREGATE_OPTIONS)} onChange={handleAggregateOptionChange} value={annotation} />
          </div>
        </div>
      </div>
      { dataToDisplay && dateRangeIds?.length && dateRangeIds.map( (rangeId, index) => {
        return (
          <div className="aggregate-row-container-values aggregate-values-header">
            { showHeader && (
              <div className="aggregate-row-header">
                <span>{dateRangeIdToLabelMap[rangeId]}</span>
              </div>
            )}

            <div className="aggregate-value-row">
              <Progress 
                percent={dataToDisplay?.[rangeId]?.percent || 0} 
                className="progress-right-container"
                format={percent => (
                  <span style={{ color: percent == 0 ? '#000000' : getTextColorBasedOnBg(dataToDisplay?.colors?.[index]), fontSize: 11, fontWeight: 'bold' }}>
                    {`${dataToDisplay?.[rangeId]?.rawValue.toFixed(dataToDisplay?.decimal_places) || 0} (${percent.toFixed(0)}%)`}
                  </span>
                )}
                strokeColor={dataToDisplay?.colors?.[index]}
              />
            </div>
          </div>
        )
        })
      }
    </>
  );
}